import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { School } from 'models/school';
import { Record as SchoolRecord } from 'models/record';
import { getRecords } from 'services/getRecords';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getConvertedScore } from 'helpers/score';
import { KIND_SERVER_TO_CLIENT_MAPPING } from 'consts/school';
import Loader from 'components/loader';
import EmailContactActions from 'components/EmailContactActions';
import './Records.scss';

interface Props {
   school: School;
}

type DateFilter = 'ALL' | 'THIS_SEASON' | 'FIVE_YEARS';
type RecordFilters = { sports: string[]; ages: string[]; genders: string[]; date: DateFilter };

const GENDER_LABELS: Record<string, string> = {
   MALE: 'Boys',
   FEMALE: 'Girls',
   MIXED: 'Mixed',
};

function getSeasonStart(now = new Date()) {
   return new Date(now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1, 8, 1);
}

function isValidDate(value?: string) {
   return Boolean(value) && !Number.isNaN(new Date(value as string).getTime());
}

function formatDate(value?: string, format: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' }) {
   return isValidDate(value) ? new Intl.DateTimeFormat('en-GB', format).format(new Date(value as string)) : 'Date not recorded';
}

function formatAthleteName(record: SchoolRecord, school: School) {
   const rawName = (record.athlete || '').trim();
   const isGroupName = /\b(team|squad|relay|group)\b/i.test(rawName);

   if (!rawName) return 'Not recorded';
   if (isGroupName) return rawName;
   const parts = rawName.split(/\s+/).filter(Boolean);
   if (school.publicStudentViewType === 'SHORTEN_FIRSTNAME_AND_LASTNAME') {
      return parts.map(part => `${part.charAt(0).toUpperCase()}.`).join(' ');
   }
   if (parts.length < 2) return parts[0];
   return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

function formatRecordValue(record: SchoolRecord) {
   try {
      return getConvertedScore(record.record, record.sport as any) || String(record.record);
   } catch {
      return String(record.record);
   }
}

function isTeamRecord(record: SchoolRecord) {
   return Boolean(record.isTeamRecord) || record.sport?.players === 'TEAM' || record.sport?.players === '2X2';
}

function getRecordAffiliation(record: SchoolRecord) {
   return String(record.affiliation || record.area || '').trim() || 'Not recorded';
}

function formatRecordHolder(record: SchoolRecord, school: School) {
   return isTeamRecord(record) ? getRecordAffiliation(record) : formatAthleteName(record, school);
}

function getRecordKey(record: SchoolRecord) {
   return [record.sportId, record.gender, [...(record.ages || [])].sort((a, b) => a - b).join(','), (record.area || '').trim().toLowerCase()].join('|');
}

function getYoungestRecordAge(record: SchoolRecord) {
   const ages = (record.ages || []).filter(age => Number.isFinite(age));
   return ages.length > 0 ? Math.min(...ages) : Number.POSITIVE_INFINITY;
}

const SchoolRecords: React.FC<Props> = ({ school }) => {
   const [records, setRecords] = useState<SchoolRecord[]>([]);
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);
   const [sportIds, setSportIds] = useState<string[]>([]);
   const [selectedAges, setSelectedAges] = useState<string[]>([]);
   const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
   const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');
   const [athleteSearch, setAthleteSearch] = useState('');
   const [sportSearch, setSportSearch] = useState('');
   const [isFiltersOpen, setIsFiltersOpen] = useState(false);
   const [draftFilters, setDraftFilters] = useState<RecordFilters>({ sports: [], ages: [], genders: [], date: 'ALL' });
   const [selectedId, setSelectedId] = useState('');
   const [showHistory, setShowHistory] = useState(false);
   const isUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;

   useEffect(() => {
      let isActive = true;
      setIsLoading(true);
      setLoadError(false);

      getRecords(school.id)
         .then((items: SchoolRecord[]) => {
            if (!isActive) return;
            const safeItems = Array.isArray(items) ? items : [];
            setRecords(safeItems);
            setSelectedId(safeItems[0]?.id || '');
         })
         .catch(() => {
            if (isActive) setLoadError(true);
         })
         .finally(() => {
            if (isActive) setIsLoading(false);
         });

      return () => {
         isActive = false;
      };
   }, [school.id]);

   const sports = useMemo(() => {
      const byId = new Map<string, string>();
      records.forEach(record => {
         if (record.sportId) byId.set(record.sportId, record.sport?.name || 'Sport');
      });
      return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
   }, [records]);

   const ages = useMemo(() => {
      const uniqueAges = new Set<number>();
      records.filter(record => !sportIds.length || sportIds.includes(record.sportId)).forEach(record => {
         (record.ages || []).forEach(item => uniqueAges.add(item));
      });
      return Array.from(uniqueAges).sort((a, b) => a - b);
   }, [records, sportIds]);

   const genders = useMemo(() => Array.from(new Set(records.map(record => String(record.gender || '')).filter(Boolean)))
      .sort((a, b) => (GENDER_LABELS[a] || a).localeCompare(GENDER_LABELS[b] || b)), [records]);

   const filteredRecords = useMemo(() => {
      const seasonStart = getSeasonStart();
      const fiveYearsAgo = new Date();
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

      const matching = records.filter(record => {
         if (sportIds.length && !sportIds.includes(record.sportId)) return false;
         if (selectedAges.length && !(record.ages || []).some(item => selectedAges.includes(String(item)))) return false;
         if (selectedGenders.length && !selectedGenders.includes(String(record.gender))) return false;
         if (athleteSearch.trim() && !`${record.athlete || ''} ${record.affiliation || ''} ${record.area || ''} ${record.teamName || ''}`.toLowerCase().includes(athleteSearch.trim().toLowerCase())) return false;
         if (dateFilter !== 'ALL' && !isValidDate(record.date)) return false;
         if (dateFilter === 'THIS_SEASON' && new Date(record.date) < seasonStart) return false;
         if (dateFilter === 'FIVE_YEARS' && new Date(record.date) < fiveYearsAgo) return false;
         return true;
      });
      if (!isUnion || showHistory) return matching.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
      const latest = new Map<string, SchoolRecord>();
      matching.forEach(record => {
         const key = getRecordKey(record);
         const previous = latest.get(key);
         if (!previous || new Date(record.date || 0).getTime() > new Date(previous.date || 0).getTime()) latest.set(key, record);
      });
      return [...latest.values()].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
   }, [records, sportIds, selectedAges, selectedGenders, athleteSearch, dateFilter, isUnion, showHistory]);

   useEffect(() => {
      if (!filteredRecords.some(record => record.id === selectedId)) {
         setSelectedId(filteredRecords[0]?.id || '');
      }
   }, [filteredRecords, selectedId]);

   const selectedRecord = filteredRecords.find(record => record.id === selectedId);
   const seasonStart = getSeasonStart();
   const recordsThisSeason = records.filter(record => isValidDate(record.date) && new Date(record.date) >= seasonStart);
   const mostRecentRecord = records
      .filter(record => isValidDate(record.date))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
   const standingRecordCount = new Set(records.map(getRecordKey)).size;
   const hasHistory = isUnion && records.length > standingRecordCount;
   const recordsByKey = useMemo(() => records.reduce((map, record) => {
      const key = getRecordKey(record);
      map.set(key, [...(map.get(key) || []), record].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()));
      return map;
   }, new Map<string, SchoolRecord[]>()), [records]);
   const currentRecordIds = useMemo(() => new Set(Array.from(recordsByKey.values()).map(items => items[0]?.id).filter(Boolean)), [recordsByKey]);
   const activeFilterCount = sportIds.length + selectedAges.length + selectedGenders.length + (dateFilter === 'ALL' ? 0 : 1);
   const hasFilters = activeFilterCount > 0 || Boolean(athleteSearch.trim());
   const sportQuery = sportSearch.trim().toLowerCase();
   const sportOptions = sportQuery ? sports.filter(sport => !draftFilters.sports.includes(sport.id) && sport.name.toLowerCase().includes(sportQuery)) : [];
   const toggle = (values: string[], value: string) => values.includes(value) ? values.filter(item => item !== value) : [...values, value];
   const openFilters = () => {
      setDraftFilters({ sports: sportIds, ages: selectedAges, genders: selectedGenders, date: dateFilter });
      setSportSearch('');
      setIsFiltersOpen(true);
   };
   const applyFilters = () => {
      setSportIds(draftFilters.sports);
      setSelectedAges(draftFilters.ages);
      setSelectedGenders(draftFilters.genders);
      setDateFilter(draftFilters.date);
      setIsFiltersOpen(false);
   };
   const contactEmail = (school.sportsDepartmentEmail || school.email || '').trim();

   const sections = useMemo(() => {
      const groups = new Map<string, SchoolRecord[]>();
      filteredRecords.forEach(record => {
         const label = [record.sport?.name || 'Sport', GENDER_LABELS[record.gender] || record.gender].filter(Boolean).join(' · ');
         groups.set(label, [...(groups.get(label) || []), record]);
      });
      return Array.from(groups, ([label, sectionRecords]) => ({
         label,
         records: [...sectionRecords].sort((left, right) => {
            const ageDifference = getYoungestRecordAge(left) - getYoungestRecordAge(right);
            return ageDifference || new Date(right.date || 0).getTime() - new Date(left.date || 0).getTime();
         }),
         isTeam: sectionRecords.every(isTeamRecord)
      }));
   }, [filteredRecords]);

   if (isLoading) {
      return <div className='school-records__loading'><Loader /></div>;
   }

   return (
      <main className='school-records'>
         <header className='school-records__hero'>
            <div className='school-records__hero-copy'>
               <span>Honours board</span>
               <h1>{isUnion ? 'Roll of honour' : 'School records'}</h1>
               <p>{isUnion ? `The latest recognised performances across ${school.name}.` : 'Every standing school record, kept and confirmed by the PE department.'}</p>
            </div>
            <div className='school-records__stats' aria-label='Records summary'>
               <div><strong>{isUnion ? standingRecordCount : records.length}</strong><span>Records standing</span></div>
               <div><strong>{recordsThisSeason.length}</strong><span>Set this season</span></div>
               <div className='school-records__latest'>
                  <strong>{mostRecentRecord ? formatRecordHolder(mostRecentRecord, school) : '—'}</strong>
                  <span>{mostRecentRecord ? `Most recent · ${formatDate(mostRecentRecord.date)}` : 'No records published'}</span>
               </div>
            </div>
         </header>

         {records.length > 0 && (
            <div className='school-records__filters'>
               <label className='school-records__search'>
                  <SearchRoundedIcon aria-hidden='true' />
                  <input value={athleteSearch} onChange={event => setAthleteSearch(event.target.value)} placeholder={isUnion ? 'Search by athlete or team' : 'Search by athlete'} />
                  {athleteSearch && <button type='button' onClick={() => setAthleteSearch('')} aria-label='Clear athlete search'><CloseRoundedIcon /></button>}
               </label>
               <div className='school-records__filterActions'>
                  {hasHistory && <div className='school-records__mode' role='group' aria-label='Record view'><button type='button' className={!showHistory ? 'is-active' : ''} onClick={() => setShowHistory(false)}>Current records</button><button type='button' className={showHistory ? 'is-active' : ''} onClick={() => setShowHistory(true)}>History</button></div>}
                  <button type='button' className='school-records__filterButton' onClick={openFilters}><TuneRoundedIcon />Filters{activeFilterCount > 0 && <b>{activeFilterCount}</b>}</button>
               </div>
            </div>
         )}

         {hasHistory && <div className={`school-records__historyNotice${showHistory ? ' is-history' : ''}`}><strong>{showHistory ? 'Viewing the record archive' : 'Historical records are available'}</strong><span>{showHistory ? 'Current records are highlighted. Earlier performances follow in date order.' : 'Open History to see how each record has changed over time.'}</span></div>}

         {loadError ? (
            <section className='school-records__empty' role='alert'>
               <EmojiEventsRoundedIcon aria-hidden='true' />
               <h2>Records could not be loaded</h2>
               <p>Please refresh the page and try again.</p>
            </section>
         ) : records.length === 0 ? (
            <section className='school-records__empty'>
               <EmojiEventsRoundedIcon aria-hidden='true' />
               <h2>No records published yet</h2>
               <p>When records are published, the honours board will appear here.</p>
               <div>
                  <Link to={isUnion ? '/tournamentResults' : '/results'}>See recent results</Link>
                  <Link to={isUnion ? '/upcomingTournaments' : '/fixtures'} className='is-secondary'>{isUnion ? 'Upcoming tournaments' : 'Upcoming fixtures'}</Link>
               </div>
            </section>
         ) : filteredRecords.length === 0 ? (
            <section className='school-records__empty'>
               <EmojiEventsRoundedIcon aria-hidden='true' />
               <h2>No matching records</h2>
               <p>{hasFilters ? 'No records match this age group and date range.' : 'No records have been published for this sport yet.'}</p>
               <button type='button' onClick={() => { setSportIds([]); setSelectedAges([]); setSelectedGenders([]); setDateFilter('ALL'); setAthleteSearch(''); }}>Clear filters</button>
            </section>
         ) : (
            <div className='school-records__content'>
               <div className='school-records__boards'>
                  {sections.map(section => (
                     <section key={section.label} className='school-records__section'>
                        <div className='school-records__section-heading'>
                           <h2>{section.label}</h2>
                           <span>{section.records.length} {section.records.length === 1 ? 'record' : 'records'}</span>
                        </div>
                        <div className={`school-records__table${section.isTeam ? ' is-team' : ' is-individual'}`}>
                           <div className='school-records__table-head' aria-hidden='true'>
                              <span>Record</span><span>{section.isTeam ? 'Team' : 'Athlete'}</span>{!section.isTeam && <span>Affiliation</span>}<span>Age</span><span>Set</span>
                           </div>
                           {section.records.map(record => {
                              const isNew = isValidDate(record.date) && new Date(record.date) >= seasonStart;
                              const isCurrent = currentRecordIds.has(record.id);
                              const previousCount = Math.max(0, (recordsByKey.get(getRecordKey(record))?.length || 1) - 1);
                              return (
                                 <button
                                    type='button'
                                    key={record.id}
                                    className={`school-records__row${record.id === selectedId ? ' is-selected' : ''}${showHistory && isCurrent ? ' is-current' : ''}`}
                                    onClick={() => setSelectedId(record.id)}
                                 >
                                    <strong>{formatRecordValue(record)}{isNew && <em>New</em>}{showHistory && isCurrent && <em className='is-current'>Current</em>}</strong>
                                    <span className='school-records__holder'>{formatRecordHolder(record, school)}{!showHistory && previousCount > 0 && <small>{previousCount} previous {previousCount === 1 ? 'record' : 'records'}</small>}</span>
                                    {!section.isTeam && <span className='school-records__affiliation'>{getRecordAffiliation(record)}</span>}
                                    <span className='school-records__age'>{getAgesGroupFormatted({ ages: [...(record.ages || [])] }, school)}</span>
                                    <time dateTime={record.date}>{formatDate(record.date)}</time>
                                 </button>
                              );
                           })}
                        </div>
                     </section>
                  ))}
                  <p className='school-records__note'>Times and distances use each sport’s official result format, matching published event results.</p>
               </div>

               {selectedRecord && (
                  <aside className='school-records__detail'>
                     <section>
                        <span className='school-records__eyebrow'>Record holder</span>
                        <strong className='school-records__detail-value'>{formatRecordValue(selectedRecord)}</strong>
                           <h2>{selectedRecord.sport?.name || (isUnion ? 'Union record' : 'School record')}</h2>
                        <dl>
                           {isTeamRecord(selectedRecord) ? (
                              <div><dt>Team</dt><dd>{getRecordAffiliation(selectedRecord)}</dd></div>
                           ) : (
                              <><div><dt>Athlete</dt><dd>{formatAthleteName(selectedRecord, school)}</dd></div><div><dt>Affiliation</dt><dd>{getRecordAffiliation(selectedRecord)}</dd></div></>
                           )}
                           <div><dt>Age group</dt><dd>{getAgesGroupFormatted({ ages: [...(selectedRecord.ages || [])] }, school)}</dd></div>
                           <div><dt>Set</dt><dd>{formatDate(selectedRecord.date, { day: 'numeric', month: 'long', year: 'numeric' })}</dd></div>
                           <div><dt>Sport</dt><dd>{selectedRecord.sport?.name || 'Not recorded'}</dd></div>
                        </dl>
                        {selectedRecord.eventId && (
                           <Link to={`/event/${selectedRecord.eventId}`} state={{ prevPath: '/records' }}>View the event <ArrowForwardRoundedIcon aria-hidden='true' /></Link>
                        )}
                     </section>
                     {contactEmail && (
                        <section className='school-records__contact'>
                           <h3>Think a record is wrong?</h3>
                           <p>Tell {isUnion ? 'the union' : 'the PE department'} what you know and they can check the result.</p>
                           <EmailContactActions
                              compact
                              email={contactEmail}
                              subject={`${school.name} record query`}
                              emailActionLabel='Open email app'
                              copyActionLabel='Copy email address'
                           />
                        </section>
                     )}
                  </aside>
               )}
            </div>
         )}
         {isFiltersOpen && <div className='school-records__filterBackdrop' onMouseDown={() => setIsFiltersOpen(false)}>
            <aside className='school-records__filterPanel' role='dialog' aria-modal='true' aria-label='Record filters' onMouseDown={event => event.stopPropagation()}>
               <header><div><span>Records</span><h2>Filter the honours board</h2></div><button type='button' onClick={() => setIsFiltersOpen(false)} aria-label='Close filters'><CloseRoundedIcon /></button></header>
               <div className='school-records__filterPanelBody'>
                  <fieldset><legend>Sports</legend><input value={sportSearch} onChange={event => setSportSearch(event.target.value)} placeholder='Search sports' />{draftFilters.sports.length > 0 && <div className='is-selectedOptions'>{draftFilters.sports.map(value => <button key={value} type='button' onClick={() => setDraftFilters(current => ({ ...current, sports: current.sports.filter(item => item !== value) }))}>{sports.find(item => item.id === value)?.name || value}<CloseRoundedIcon /></button>)}</div>}{sportOptions.length > 0 && <div>{sportOptions.map(sport => <button key={sport.id} type='button' onClick={() => { setDraftFilters(current => ({ ...current, sports: [...current.sports, sport.id] })); setSportSearch(''); }}>{sport.name}</button>)}</div>}</fieldset>
                  <fieldset><legend>Age groups</legend><div>{ages.map(item => { const value = String(item); return <button key={value} type='button' className={draftFilters.ages.includes(value) ? 'is-selected' : ''} onClick={() => setDraftFilters(current => ({ ...current, ages: toggle(current.ages, value) }))}>{getAgesGroupFormatted({ ages: [item] }, school)}</button>; })}</div></fieldset>
                  <fieldset><legend>Gender</legend><div>{genders.map(value => <button key={value} type='button' className={draftFilters.genders.includes(value) ? 'is-selected' : ''} onClick={() => setDraftFilters(current => ({ ...current, genders: toggle(current.genders, value) }))}>{GENDER_LABELS[value] || value}</button>)}</div></fieldset>
                  <fieldset><legend>Record date</legend><div>{([['ALL', 'Any time'], ['THIS_SEASON', 'This season'], ['FIVE_YEARS', 'Last five years']] as [DateFilter, string][]).map(([value, label]) => <button key={value} type='button' className={draftFilters.date === value ? 'is-selected' : ''} onClick={() => setDraftFilters(current => ({ ...current, date: value }))}>{label}</button>)}</div></fieldset>
               </div>
               <footer><button type='button' className='is-clear' onClick={() => setDraftFilters({ sports: [], ages: [], genders: [], date: 'ALL' })}>Clear all</button><div><button type='button' onClick={() => setIsFiltersOpen(false)}>Cancel</button><button type='button' className='is-apply' onClick={applyFilters}>Apply filters</button></div></footer>
            </aside>
         </div>}
      </main>
   );
};

export default SchoolRecords;
